import type { ChainModifiers, Entry, EntryFieldTypes, EntrySkeletonType, LocaleCode } from 'contentful'
import type { TypeLessonDataSkeleton } from './TypeLessonData'

export interface TypeLessonResourceFields {
  title: EntryFieldTypes.Symbol
  resourceType: EntryFieldTypes.Symbol<
    | 'cursive'
    | 'es_student_pdf'
    | 'handwriting'
    | 'handwriting_nsw'
    | 'handwriting_qld'
    | 'handwriting_vic'
    | 'phonics'
    | 'problem_solving'
    | 'skills'
    | 'student_pdf'
    | 'teacher_pdf'
    | 'teaching_powerpoint'
    | 'weekly_teaching_guide'
  >
  description?: EntryFieldTypes.Text
  cover?: EntryFieldTypes.AssetLink
  file?: EntryFieldTypes.AssetLink
  videoUrl?: EntryFieldTypes.Symbol
  lessonData: EntryFieldTypes.EntryLink<TypeLessonDataSkeleton>
  domains: EntryFieldTypes.Array<
    EntryFieldTypes.Symbol<'all' | 'au' | 'ca' | 'cn' | 'in' | 'kr' | 'nz' | 'th' | 'uk' | 'us' | 'za'>
  >
  accent?: EntryFieldTypes.Symbol<'au' | 'ca' | 'uk' | 'us'>
  curriculum?: EntryFieldTypes.Array<EntryFieldTypes.Symbol<'au' | 'ca' | 'uk' | 'us'>>
}

export type TypeLessonResourceSkeleton = EntrySkeletonType<TypeLessonResourceFields, 'lessonResource'>
export type TypeLessonResource<Modifiers extends ChainModifiers, Locales extends LocaleCode = LocaleCode> = Entry<
  TypeLessonResourceSkeleton,
  Modifiers,
  Locales
>

export function isTypeLessonResource<Modifiers extends ChainModifiers, Locales extends LocaleCode>(
  entry: Entry<EntrySkeletonType, Modifiers, Locales>,
): entry is TypeLessonResource<Modifiers, Locales> {
  return entry.sys.contentType.sys.id === 'lessonResource'
}

export type TypeLessonResourceWithoutLinkResolutionResponse = TypeLessonResource<'WITHOUT_LINK_RESOLUTION'>
export type TypeLessonResourceWithoutUnresolvableLinksResponse = TypeLessonResource<'WITHOUT_UNRESOLVABLE_LINKS'>
export type TypeLessonResourceWithAllLocalesResponse<Locales extends LocaleCode = LocaleCode> = TypeLessonResource<
  'WITH_ALL_LOCALES',
  Locales
>
export type TypeLessonResourceWithAllLocalesAndWithoutLinkResolutionResponse<Locales extends LocaleCode = LocaleCode> =
  TypeLessonResource<'WITHOUT_LINK_RESOLUTION' | 'WITH_ALL_LOCALES', Locales>
export type TypeLessonResourceWithAllLocalesAndWithoutUnresolvableLinksResponse<
  Locales extends LocaleCode = LocaleCode,
> = TypeLessonResource<'WITHOUT_UNRESOLVABLE_LINKS' | 'WITH_ALL_LOCALES', Locales>
